import { useFormik } from "formik";
import * as Yup from "yup";
import usePost from "../../../hooks/api/usePost";
import apiRequest from "../../../utils/apiRequest";
import { useEffect, useState } from "react";

const ConfirmOrder = ({ order, closeModal, fetchData }) => {
  const { isLoading, post, error, serverErrors } = usePost(
    apiRequest.confirmOrderProducts
  );
  const [formError, setFormError] = useState("");

  const formik = useFormik({
    initialValues: {
      productData: order.products.map((p) => ({
        title: `${p.product?.brand?.name ?? ""} ${p.product?.title ?? ""}`,
        id: p.product._id || p.product,
        isConfirmed: p.isConfirmed ?? false,
        imei: p.imei ?? [],
      })),
    },
    validationSchema: Yup.object({
      productData: Yup.array().of(
        Yup.object({
          id: Yup.string().required(),
          isConfirmed: Yup.boolean(),
        })
      ),
    }),
    onSubmit: async (values) => {
      setFormError(""); // clear previous
      const unconfirmed = values.productData.filter((p) => !p.isConfirmed);

      if (unconfirmed.length > 0) {
        setFormError("⚠️ Please confirm all products before submitting.");
        formik.setTouched({
          productData: values.productData.map(() => ({ isConfirmed: true })),
        });
        return;
      }

      const payload = {
        productData: values.productData.map((p) => ({
          id: p.id,
          isConfirmed: p.isConfirmed,
        })),
      };

      const res = await post({
        data: { id: order._id, body: payload },
      });

      if (res?.response) {
        fetchData();
        closeModal();
      }
    },
  });

  return (
    <div className="max-w-3xl p-6 bg-white rounded-lg shadow-md text-[13px] text-gray-700">
      {/* Header */}
      <h2 className="mb-4 text-lg font-semibold text-emerald-700">
        Confirm Received Products
      </h2>

      {/* Instructions */}
      <ul className="pl-5 mb-4 text-sm text-gray-600 list-disc">
        <li>
          Verify the IMEI numbers shown below.{" "}
          <strong>Dial *#06# to check IMEI</strong>
        </li>
        <li>Check the box for each product you have received.</li>
        <li>
          <strong>All products must be confirmed</strong> to proceed.
        </li>
      </ul>

      <form onSubmit={formik.handleSubmit}>
        {formik.values.productData.map((product, index) => {
          const showError =
            !product.isConfirmed &&
            formik.touched.productData?.[index]?.isConfirmed;

          return (
            <div
              key={product.id}
              className={`mb-4 p-4 border rounded-md shadow-sm transition-all ${
                showError
                  ? "border-red-500 bg-red-50"
                  : "border-emerald-300 bg-emerald-50"
              }`}
            >
              <div className="text-[14px] font-medium text-gray-800 mb-1">
                {product.title}
              </div>
              {product.imei.map((imei, i) => (
                <div key={i} className="text-xs text-gray-600">
                  IMEI {i + 1}: {imei}
                </div>
              ))}

              <div className="mt-2">
                <label className="inline-flex items-center text-sm">
                  <input
                    type="checkbox"
                    name={`productData.${index}.isConfirmed`}
                    checked={product.isConfirmed}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="mr-2 accent-green-600"
                  />
                  I confirm this product and IMEI is correct
                </label>
              </div>

              {showError && (
                <div className="mt-1 text-xs text-red-600">
                  Please confirm this product to proceed.
                </div>
              )}
            </div>
          );
        })}

        {/* Show overall error */}
        {formError && (
          <div className="mt-2 text-sm font-medium text-red-600">
            {formError}
          </div>
        )}

        {error && (
          <div className="mt-2 text-xs text-red-600">{error.message}</div>
        )}
        {serverErrors && (
          <div className="mt-2 text-xs text-red-600">{serverErrors}</div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={closeModal}
            className="px-3 py-1 rounded bg-gray-300 hover:bg-gray-400 text-[13px]"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-1 rounded bg-green-600 hover:bg-green-700 text-white text-[13px]"
            disabled={isLoading}
          >
            {isLoading ? "Confirming..." : "Confirm Products"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConfirmOrder;
